/**
 * Apply a Director-made "turn cost auto-confirm OFF" change to the
 * client-owned toggles (feature=project-setup-card, concern=auto-confirm-off).
 *
 * The auto-confirm toggles live in this browser's localStorage and are sent
 * with every chat request, so the backend cannot flip them for the user. When
 * the Director calls `update_project_config(auto_confirm_*: false)` the backend
 * emits `project_config_changed` with `auto_confirm_off`; this hook turns the
 * matching toggles off so the next request carries `false`. It only ever turns
 * a toggle OFF — turning auto-confirm on is user-only.
 */

import { useEffect, useRef } from 'react'
import { subscribeProjectConfigChanged } from '../components/canvas-v2/projectSettingsEvents'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:project-setup-card:auto-confirm-off')

export interface DirectorAutoConfirmOffOptions {
  projectId: string | undefined
  onImageOff: (value: false) => void
  onVideoOff: (value: false) => void
}

export function useDirectorAutoConfirmOff({
  projectId,
  onImageOff,
  onVideoOff,
}: DirectorAutoConfirmOffOptions): void {
  const handlers = useRef({ onImageOff, onVideoOff })
  handlers.current = { onImageOff, onVideoOff }

  useEffect(() => {
    if (!projectId) return undefined
    return subscribeProjectConfigChanged((detail) => {
      if (detail.projectId !== projectId) return
      const off = detail.autoConfirmOff ?? []
      if (off.length === 0) return
      if (off.includes('image')) handlers.current.onImageOff(false)
      if (off.includes('video')) handlers.current.onVideoOff(false)
      log({
        event: 'applied',
        project_id: projectId,
        actor: detail.actor,
        auto_confirm_off: off.join(','),
      })
    })
  }, [projectId])
}
